import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Divider
} from '@mui/material'
import {
  Edit,
  ArrowBack,
  Description,
  Business,
  People,
  MenuBook,
  Gavel,
  Timeline,
  CheckCircle,
  AccountTree,
  PictureAsPdf,
  Download,
  Visibility,
  ChangeHistory,
  Comment
} from '@mui/icons-material'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'
import mermaid from 'mermaid'
import ComentarioComponent from '../comentarios/ComentarioComponent'
import ControlCambios from '../comentarios/ControlCambios'

// Inicializar Mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'Arial, sans-serif'
})

// Función para formatear definiciones con negritas
const formatDefiniciones = (definiciones) => {
  if (!definiciones) return null
  
  // Buscar patrones como "Palabra:" o "Palabra definición:" seguido de explicación
  const parts = definiciones.split(';')
  
  return parts.map((part, index) => {
    const trimmed = part.trim()
    if (!trimmed) return null
    
    // Buscar el patrón "Palabra:" o "Término:"
    const colonIndex = trimmed.indexOf(':')
    if (colonIndex > 0) {
      const termino = trimmed.substring(0, colonIndex).trim()
      const definicion = trimmed.substring(colonIndex + 1).trim()
      
      return (
        <span key={index}>
          <strong>{termino}:</strong> {definicion}
          {index < parts.length - 1 && '; '}
        </span>
      )
    }
    
    // Si no encuentra ":", mostrar el texto normal
    return (
      <span key={index}>
        {trimmed}
        {index < parts.length - 1 && '; '}
      </span>
    )
  })
}

// Función para formatear normatividad con enlaces clickeables
const formatNormatividad = (normatividadTexto, normatividades, onViewNormatividad) => {
  if (!normatividadTexto) return null
  
  // Dividir el texto por comas o punto y coma
  const parts = normatividadTexto.split(/[,;]+/).map(part => part.trim()).filter(part => part.length > 0)
  
  return parts.map((part, index) => {
    // Buscar coincidencia con normatividades predefinidas
    const normFound = normatividades.find(norm => 
      norm.titulo && (
        part.toLowerCase().includes(norm.titulo.toLowerCase()) ||
        norm.titulo.toLowerCase().includes(part.toLowerCase())
      )
    )
    
    if (normFound && normFound.archivo_url) {
      return (
        <span key={index}>
          <Button
            variant="text"
            size="small"
            onClick={() => onViewNormatividad(normFound)}
            sx={{ 
              textTransform: 'none',
              p: 0,
              minWidth: 'auto',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'underline',
                backgroundColor: 'transparent'
              }
            }}
          >
            {part}
          </Button>
          {index < parts.length - 1 && ', '}
        </span>
      )
    }
    
    // Si no hay coincidencia, mostrar texto normal
    return (
      <span key={index}>
        {part}
        {index < parts.length - 1 && ', '}
      </span>
    )
  })
}

// Componente para mostrar solo la vista previa del diagrama
const DiagramPreview = ({ mermaidCode }) => {
  const [svgContent, setSvgContent] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (mermaidCode && mermaidCode.trim()) {
      renderDiagram()
    }
  }, [mermaidCode])

  const renderDiagram = async () => {
    setLoading(true)
    setError('')
    
    try {
      const diagramId = `mermaid-preview-${Date.now()}`
      const { svg } = await mermaid.render(diagramId, mermaidCode)
      setSvgContent(svg)
    } catch (err) {
      console.error('Error renderizando diagrama:', err)
      setError('Error al renderizar el diagrama')
      setSvgContent('')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    )
  }

  if (!svgContent) {
    return (
      <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
        No hay diagrama disponible
      </Typography>
    )
  }

  return (
    <Box 
      sx={{ 
        textAlign: 'center',
        '& svg': {
          maxWidth: '100%',
          height: 'auto'
        }
      }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  )
}

const ProcedimientoView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, hasPermission, isAdmin, isEditor, canEditGestion } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [procedimiento, setProcedimiento] = useState(null)

  // Estados para modal de normatividad PDF
  const [openPdfModal, setOpenPdfModal] = useState(false)
  const [selectedPdfDoc, setSelectedPdfDoc] = useState(null)
  const [normatividades, setNormatividades] = useState([])

  // Estado para las tabs
  const [currentTab, setCurrentTab] = useState(0)

  useEffect(() => {
    loadProcedimiento()
    loadNormatividades()
  }, [id])

  const loadNormatividades = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get('/api/normatividad', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setNormatividades(response.data || [])
    } catch (error) {
      console.error('Error al cargar normatividades:', error)
    }
  }

  const handleViewNormatividad = (normatividad) => {
    setSelectedPdfDoc(normatividad)
    setOpenPdfModal(true)
  }

  const loadProcedimiento = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get(`/api/procedimientos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      setProcedimiento(response.data)
    } catch (error) {
      setError('Error al cargar procedimiento: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'aprobado': return 'success'
      case 'revision': return 'warning'
      case 'borrador': return 'info'
      case 'archivado': return 'default'
      default: return 'default'
    }
  }

  const getCategoriaLabel = (categoria) => {
    const categorias = {
      'ley': 'Ley',
      'decreto': 'Decreto', 
      'resolucion': 'Resolución',
      'circular': 'Circular',
      'directiva': 'Directiva',
      'norma_tecnica': 'Norma Técnica',
      'lineamiento': 'Lineamiento',
      'politica': 'Política'
    }
    return categorias[categoria] || categoria
  }

  const canEdit = () => {
    if (!procedimiento) return false
    // Admin siempre puede editar
    if (user?.rol === 'administrador') return true
    // Editores y consultores pueden editar si tienen permiso en la gestión
    if (user?.rol === 'editor' || user?.rol === 'consultor') {
      return canEditGestion(procedimiento.gestion_id)
    }
    return false
  }

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get(`/api/export/procedimientos/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })
      
      // Crear URL para la descarga
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${procedimiento.codigo}_${procedimiento.titulo}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exportando PDF:', error)
      setError('Error al generar PDF: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleExportDiagram = async () => {
    if (!procedimiento.diagrama?.mermaid_code) {
      setError('No hay diagrama disponible para exportar')
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get(`/api/export/procedimientos/${id}/diagrama-png`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })
      
      // Crear URL para la descarga
      const blob = new Blob([response.data], { type: 'image/png' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `diagrama_${procedimiento.codigo}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exportando diagrama:', error)
      setError('Error al exportar diagrama: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handlePublicar = async () => {
    if (!window.confirm('¿Está seguro de aprobar y publicar este procedimiento en el portal público?')) {
      return
    }
    try {
      const token = localStorage.getItem('sgc_token')
      await axios.post(`/api/procedimientos/${id}/publicar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      await loadProcedimiento()
    } catch (error) {
      console.error('Error publicando procedimiento:', error)
      setError('Error al publicar: ' + (error.response?.data?.detail || error.message))
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando procedimiento...</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/procedimientos')}
        >
          Volver a Procedimientos
        </Button>
      </Box>
    )
  }

  if (!procedimiento) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Procedimiento no encontrado
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/procedimientos')}
        >
          Volver a Procedimientos
        </Button>
      </Box>
    )
  }

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue)
  }

  // Función para renderizar el tab de información del procedimiento
  const renderProcedimientoContent = () => (
    <Grid container spacing={2}>
        {/* Información General */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}>
            <CardHeader
              title="Información General"
              avatar={<Business />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Proceso
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {procedimiento.proceso}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Gestión
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {procedimiento.gestion_nombre} ({procedimiento.gestion_codigo})
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Objetivo
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {procedimiento.objetivo}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Alcance
                  </Typography>
                  <Typography variant="body1">
                    {procedimiento.alcance}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Componente de comentarios para Información General */}
          <ComentarioComponent
            procedimientoId={parseInt(id)}
            componente="informacion_general"
            title="Información General"
          />

          {/* Definiciones */}
          {procedimiento.definiciones && (
            <>
              <Card sx={{ mb: 2 }}>
                <CardHeader
                  title="Definiciones"
                  avatar={<MenuBook />}
                />
                <CardContent>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                    {formatDefiniciones(procedimiento.definiciones)}
                  </Typography>
                </CardContent>
              </Card>

              {/* Componente de comentarios para Definiciones */}
              <ComentarioComponent
                procedimientoId={parseInt(id)}
                componente="definiciones"
                title="Definiciones"
              />
            </>
          )}

          {/* Normatividad */}
          {procedimiento.normatividad && (
            <>
              <Card sx={{ mb: 2 }}>
                <CardHeader
                  title="Marco Normativo"
                  avatar={<Gavel />}
                />
                <CardContent>
                  <Typography variant="body1">
                    {formatNormatividad(procedimiento.normatividad, normatividades, handleViewNormatividad)}
                  </Typography>
                </CardContent>
              </Card>

              {/* Componente de comentarios para Marco Normativo */}
              <ComentarioComponent
                procedimientoId={parseInt(id)}
                componente="marco_normativo"
                title="Marco Normativo"
              />
            </>
          )}

          {/* Actividades */}
          {procedimiento.actividades && procedimiento.actividades.length > 0 && (
            <>
              <Card sx={{ mb: 2 }}>
                <CardHeader
                  title="Actividades del Procedimiento"
                  avatar={<Timeline />}
                />
                <CardContent>
                  <List>
                    {procedimiento.actividades.map((actividad, index) => (
                      <ListItem key={actividad.id} divider={index < procedimiento.actividades.length - 1}>
                        <ListItemIcon>
                          <CheckCircle color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          secondaryTypographyProps={{ component: 'div' }}
                          primary={
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {index + 1}. {actividad.titulo}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} component="div">
                                {actividad.descripcion}
                              </Typography>

                              {actividad.informacion_soporte && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" color="primary" fontWeight="bold">
                                    Información de Soporte:
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" component="div">
                                    {actividad.informacion_soporte}
                                  </Typography>
                                </Box>
                              )}

                              {actividad.informacion_generada && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" color="primary" fontWeight="bold">
                                    Información Generada:
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" component="div">
                                    {actividad.informacion_generada}
                                  </Typography>
                                </Box>
                              )}

                              {actividad.responsables && actividad.responsables.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="primary" fontWeight="bold" sx={{ mr: 1 }}>
                                    Responsables:
                                  </Typography>
                                  <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {actividad.responsables.map((resp, idx) => (
                                      <Chip
                                        key={idx}
                                        label={resp.nombre}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>

              {/* Componente de comentarios para Actividades */}
              <ComentarioComponent
                procedimientoId={parseInt(id)}
                componente="actividades"
                title="Actividades del Procedimiento"
              />
            </>
          )}

          {/* Diagrama de Flujo */}
          {procedimiento.diagrama && (
            <>
              <Card sx={{ mb: 2 }}>
                <CardHeader
                  title="Diagrama de Flujo (Flujograma)"
                  avatar={<AccountTree />}
                />
                <CardContent>
                  <DiagramPreview
                    mermaidCode={procedimiento.diagrama.mermaid_code}
                  />
                </CardContent>
              </Card>

              {/* Componente de comentarios para Diagrama */}
              <ComentarioComponent
                procedimientoId={parseInt(id)}
                componente="diagrama"
                title="Diagrama de Flujo"
              />
            </>
          )}
        </Grid>

        {/* Panel lateral */}
        <Grid item xs={12} md={4}>
          {/* Responsables */}
          <Card sx={{ mb: 2 }}>
            <CardHeader 
              title="Responsables"
              avatar={<People />}
            />
            <CardContent>
              {procedimiento.responsables && procedimiento.responsables.length > 0 ? (
                <Box>
                  {procedimiento.responsables.map((responsable) => (
                    <Chip
                      key={responsable.id}
                      label={responsable.nombre}
                      variant="outlined"
                      color="primary"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary">
                  No hay responsables asignados
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Información técnica */}
          <Card>
            <CardHeader 
              title="Información Técnica"
              avatar={<Description />}
            />
            <CardContent>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Creado
                  </Typography>
                  <Typography variant="body2">
                    {new Date(procedimiento.created_at).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Actualizado
                  </Typography>
                  <Typography variant="body2">
                    {new Date(procedimiento.updated_at).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
    </Grid>
  )

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Description sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1">
              {procedimiento.titulo}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip label={procedimiento.codigo} color="primary" variant="outlined" />
              <Chip
                label={procedimiento.estado.toUpperCase()}
                color={getEstadoColor(procedimiento.estado)}
                size="small"
              />
              <Chip label={`v${procedimiento.version}`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/procedimientos')}
          >
            Volver
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdf />}
            onClick={handleExportPDF}
            color="secondary"
          >
            Generar PDF
          </Button>
          {procedimiento.diagrama?.mermaid_code && (
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportDiagram}
              color="info"
            >
              Exportar Diagrama
            </Button>
          )}
          {canEdit() && procedimiento.estado !== 'aprobado' && (
            <Button
              variant="outlined"
              startIcon={<CheckCircle />}
              onClick={handlePublicar}
              color="success"
            >
              Publicar
            </Button>
          )}
          {canEdit() && (
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => navigate(`/procedimientos/${id}/editar`)}
            >
              Editar
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} aria-label="procedure tabs">
          <Tab
            icon={<Description />}
            label="Procedimiento"
            iconPosition="start"
          />
          {(isAdmin || isEditor || user?.rol === 'consultor') && (
            <Tab
              icon={<ChangeHistory />}
              label="Control de Cambios"
              iconPosition="start"
            />
          )}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && renderProcedimientoContent()}
      {currentTab === 1 && (isAdmin || isEditor || user?.rol === 'consultor') && (
        <ControlCambios procedimientoId={parseInt(id)} />
      )}

      {/* Modal de vista previa del PDF de normatividad */}
      <Dialog 
        open={openPdfModal} 
        onClose={() => setOpenPdfModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PictureAsPdf color="error" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6">
                {selectedPdfDoc?.titulo}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedPdfDoc?.categoria && getCategoriaLabel(selectedPdfDoc.categoria)} 
                {selectedPdfDoc?.numero && ` • ${selectedPdfDoc.numero}`}
                {selectedPdfDoc?.fecha_expedicion && ` • ${selectedPdfDoc.fecha_expedicion}`}
              </Typography>
            </Box>
            <Button
              startIcon={<Download />}
              onClick={() => {
                if (selectedPdfDoc?.archivo_url) {
                  const link = document.createElement('a')
                  link.href = selectedPdfDoc.archivo_url
                  link.download = selectedPdfDoc.archivo_nombre || selectedPdfDoc.titulo
                  link.click()
                }
              }}
              size="small"
            >
              Descargar
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {selectedPdfDoc?.archivo_url ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {selectedPdfDoc.descripcion && (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Descripción:</strong> {selectedPdfDoc.descripcion}
                  </Typography>
                  {selectedPdfDoc.entidad_emisora && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>Entidad Emisora:</strong> {selectedPdfDoc.entidad_emisora}
                    </Typography>
                  )}
                </Box>
              )}
              <Box sx={{ flexGrow: 1, position: 'relative' }}>
                <iframe
                  src={`${selectedPdfDoc.archivo_url}#toolbar=1&navpanes=1&scrollbar=1`}
                  width="100%"
                  height="100%"
                  style={{ 
                    border: 'none',
                    minHeight: '500px'
                  }}
                  title={`Vista previa: ${selectedPdfDoc.titulo}`}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '400px',
              flexDirection: 'column',
              gap: 2
            }}>
              <PictureAsPdf sx={{ fontSize: 64, color: 'grey.400' }} />
              <Typography color="text.secondary">
                No hay documento disponible para mostrar
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPdfModal(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProcedimientoView